import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontal snap-scroll banner used on the homepage to promote current
 * tournaments and events. Each slide is a wide 16:9 banner image; when a
 * slide only has one CTA, the whole image links out. When it has multiple
 * CTAs (e.g. Beginner + Novice signup), buttons are overlaid on the image.
 *
 * Auto-advances every 6s, pauses on hover/touch. Dots + prev/next arrows
 * (desktop only) let the user drive it manually. Mobile users just swipe.
 */

const SLIDES = [
  {
    id: "halloween-flyers",
    label: "Haunted Fun Fair & Spooky '80s",
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/0b32fecf7a49dd69_Neon%20Halloween%20Carnival%20and%20%E2%80%9980s%20Disco%20Flyers.png",
    // Composite banner contains both event flyers side-by-side. Each flyer
    // has its own JOIN NOW button in-image, so we overlay two hotspots —
    // one per event — routing to the correct JotForm signup.
    hotspots: [
      {
        id: "join-haunted-fun-fair",
        label: "Join Haunted Fun Fair",
        href: "https://form.jotform.com/assendaevents/haunted-fun-fair",
        left: 5.9,
        top: 79.8,
        width: 16.8,
        height: 7.3,
      },
      {
        id: "join-spooky-80s",
        label: "Join Spooky 80's",
        href: "https://form.jotform.com/assendaevents/assenda-spooky-80s-pickleball",
        left: 54.3,
        top: 81.5,
        width: 16.9,
        height: 7.2,
      },
    ],
  },
];

const AnnouncementsCarousel = () => {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);
  const paused = useRef(false);

  // Track which slide is currently in view (nearest to the horizontal
  // centre of the scroller). Cheap on scroll — no observers needed.
  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll("[data-slide]"));
    if (!cards.length) return;
    const centre = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    cards.forEach((c, i) => {
      const mid = c.offsetLeft + c.offsetWidth / 2;
      const dist = Math.abs(mid - centre);
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    });
    setActive(best);
  }, []);

  const scrollTo = useCallback((index) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelectorAll("[data-slide]")[index];
    if (card) {
      el.scrollTo({ left: card.offsetLeft - 8, behavior: "smooth" });
    }
  }, []);

  // Auto-advance every 6s unless the user is interacting.
  useEffect(() => {
    const id = setInterval(() => {
      if (paused.current) return;
      setActive((prev) => {
        const next = (prev + 1) % SLIDES.length;
        scrollTo(next);
        return next;
      });
    }, 6000);
    return () => clearInterval(id);
  }, [scrollTo]);

  return (
    <section
      data-testid="announcements-carousel"
      className="relative w-full max-w-3xl mx-auto px-2 sm:px-4"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onTouchStart={() => (paused.current = true)}
      onTouchEnd={() => setTimeout(() => (paused.current = false), 4000)}
    >
      <div className="flex items-baseline justify-between mb-2 sm:mb-3">
        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#8cc63f]">
          Upcoming Events
        </p>
        {SLIDES.length > 1 && (
          <p className="hidden sm:block text-[10px] uppercase tracking-widest text-[#a1a1aa]">
            Swipe to see more
          </p>
        )}
      </div>

      <div className="relative group">
        {/* Prev / Next (desktop only, hidden on touch) */}
        {SLIDES.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous announcement"
              data-testid="announcement-prev"
              onClick={() =>
                scrollTo((active - 1 + SLIDES.length) % SLIDES.length)
              }
              className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-9 h-9 rounded-full bg-black/70 border border-white/20 text-white/80 hover:text-white hover:bg-black opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next announcement"
              data-testid="announcement-next"
              onClick={() => scrollTo((active + 1) % SLIDES.length)}
              className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-9 h-9 rounded-full bg-black/70 border border-white/20 text-white/80 hover:text-white hover:bg-black opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth gap-3 pb-2 -mx-4 sm:mx-0 px-4 sm:px-0"
          style={{ scrollbarWidth: "none" }}
        >
          {SLIDES.map((s) => (
            <div
              key={s.id}
              data-slide={s.id}
              data-testid={`announcement-slide-${s.id}`}
              className="snap-center shrink-0 w-full sm:w-[85%]"
            >
              <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-white/10 bg-black">
                <img
                  src={s.image}
                  alt={s.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Invisible hotspots overlaid on the in-image JOIN buttons.
                    Using percentage coordinates so they scale with the
                    banner at any width. Subtle hover glow gives feedback. */}
                {s.hotspots.map((h) => (
                  <a
                    key={h.id}
                    href={h.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${s.label} — ${h.label}`}
                    data-testid={`announcement-cta-${s.id}-${h.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute rounded-full transition-shadow hover:shadow-[0_0_0_4px_rgba(140,198,63,0.55)] focus:outline-none focus:shadow-[0_0_0_4px_rgba(140,198,63,0.75)]"
                    style={{
                      left: `${h.left}%`,
                      top: `${h.top}%`,
                      width: `${h.width}%`,
                      height: `${h.height}%`,
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Dots */}
        {SLIDES.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Go to ${s.label}`}
                data-testid={`announcement-dot-${i}`}
                onClick={() => scrollTo(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? "w-6 bg-[#8cc63f]" : "w-1.5 bg-white/30"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AnnouncementsCarousel;
